import { LoadingButton } from '@mui/lab';
import {
	Button,
	CardContent,
	CardHeader,
	Grid,
	Link as MuiLink,
	TextField as MuiTextField,
	Stack,
	Typography,
} from '@mui/material';
import { Alert } from 'components/ui/alert';
import { TextField } from 'components/ui/fields';
import { useToast } from 'components/ui/toast';
import { Formik } from 'formik';
import { OrganizationProfile } from 'middleware-types';
import { Link } from 'react-router-dom';
// TODO: When testable, update deprecated MutationErrorText with ResponseHasErrors and HandleNoResponse pattern
import { OrgAvatarUploadButton } from 'components/ui/emblem/avatar-upload-button';
import { OrgEmblemBanner } from 'components/ui/emblem/emblem-banner';
import { OrganizationProfileUpdate } from 'middleware-types';
import { handleNoResponse, responseHasErrors } from 'utils/errors';
import { Permission } from 'utils/permissions';
import { useAssociateUser } from 'utils/useAssociateUser';
import { useValidation } from 'utils/useValidation';
import { useOrgProfileHeaderMutation, useOrgProfileHeaderQuery } from './hooks';

/**
 * useOrgProfileHeader() - Hook for editing the OrgProfileHeader
 *
 * @param {string} userId
 * @return {*}
 */
const useOrgProfileHeaderEdit = (orgProfile: OrganizationProfile, onCancel: () => void) => {
	const toast = useToast();

	// initialFocusedField references which Formik field name will be focused when edit mode is opened,
	// for instance companyTitle when clicking the Add Title link.
	const { profile } = useOrgProfileHeaderQuery(orgProfile.id);
	const { updateOrgProfile, error } = useOrgProfileHeaderMutation(orgProfile.id);
	const baseValidation = useValidation('OrganizationProfileUpdate');

	const initialValues: OrganizationProfileUpdate = {
		displayName: orgProfile.displayName,
		websiteUrl: '',
		avatarUploadToken: '',
		bannerUploadToken: '',
		businessLine: '',
		specifiedBusinessLine: '',
		about: '',
	};

	const onSubmit = async (values: OrganizationProfileUpdate) => {
		await updateOrgProfile({
			displayName: values.displayName,
		})
			.then(async (res) => {
				if (responseHasErrors(res.errors, { toast })) {
					return false;
				}
				toast.push('Profile updated successfully.', { variant: 'success' });
				onCancel();
				return true;
			})
			.catch(() => {
				handleNoResponse({ toast });
				return false;
			});
	};

	return {
		profile,
		error,
		initialValues,
		onSubmit,
		validation: baseValidation.schema,
	};
};

/**
 * OrgHeaderEdit is the edit form for the Profile Header
 */
export const OrgProfileHeaderEdit = (props: {
	profile: OrganizationProfile;
	onCancel: () => void;
	initialFocusedField?: string | null;
}) => {
	const { onSubmit, initialValues, validation, error } = useOrgProfileHeaderEdit(
		props.profile,
		props.onCancel
	);
	const { hasPermission } = useAssociateUser(props.profile.id);
	const canView = hasPermission(Permission.Org_Account_R);

	return (
		<Formik<OrganizationProfileUpdate>
			initialValues={initialValues}
			onSubmit={onSubmit}
			enableReinitialize
			validationSchema={validation}>
			{(fProps) => (
				<>
					<OrgEmblemBanner id={props.profile.id} enableUpload />
					<CardHeader
						// Move the avatar in edit mode to overlay on top of the cardmedia.
						sx={{
							'& .MuiCardHeader-avatar': {
								marginTop: '-9rem',
								zIndex: 1,
							},
						}}
						avatar={<OrgAvatarUploadButton id={props.profile.id} />}
						action={
							<Stack direction="row" spacing={1}>
								<Button
									variant="outlined"
									onClick={() => {
										fProps.resetForm();
										props.onCancel();
									}}>
									Cancel
								</Button>
								<LoadingButton
									color="primary"
									variant="contained"
									loading={fProps.isSubmitting}
									onClick={fProps.submitForm}
									disabled={
										!fProps.isValid || !fProps.dirty || fProps.isSubmitting
									}>
									{`Save`}
								</LoadingButton>
							</Stack>
						}
					/>
					<CardContent>
						{error && <Alert error={error} />}
						<Stack>
							<Grid container spacing={1} columnSpacing={2}>
								<Grid xs={12} item>
									<TextField
										label="Organization Display Name"
										name="displayName"
										fullWidth
										required
										autoFocus={props.initialFocusedField === 'displayName'}
									/>
								</Grid>

								<Grid xs={12} item>
									<MuiTextField
										label="Primary Location"
										fullWidth
										disabled
										value={props.profile.displayLocation}
									/>
									{canView && (
										<Typography variant="caption">
											This is based on your primary address. Edit your
											addresses in&nbsp;
											<MuiLink
												to={`account`}
												color="primary"
												component={Link}
												underline="hover">
												Account Settings.
											</MuiLink>
										</Typography>
									)}
								</Grid>
							</Grid>
						</Stack>
					</CardContent>
				</>
			)}
		</Formik>
	);
};
